// Browser implementation of the bridge the desktop app's main process provides (window.dts). The UI code is shared
// with the desktop app unchanged; everything platform-specific lives here.
import {
  ApiError, agentToolName, displayName, EventPoller, extensionOf, isRiskyAttachment, parseMailbox, runCrmAgent, safeAttachmentName,
  type AgentTool, type MailEvent,
} from '@dts/core';
import { acceptInvite, client, deviceId, enableMfa, login, logout, onSessionExpired, publicSession, sessionStore, transport, verifyMfa } from './session';
import { mcp } from './mcp';
import { uploadFile, uploadsAvailable } from './uploads';
import type { CrmApprovalRequest, DtsBridge, IpcResult, UpdateState } from './types';

type Listener<A extends unknown[]> = (...args: A) => void;

function emitter<A extends unknown[]>() {
  const set = new Set<Listener<A>>();
  return {
    on(cb: Listener<A>) {
      set.add(cb);
      return () => void set.delete(cb);
    },
    emit(...args: A) {
      set.forEach((cb) => cb(...args));
    },
    get size() {
      return set.size;
    },
  };
}

function fail(e: unknown): IpcResult<never> {
  if (e instanceof ApiError) return { ok: false, status: e.status, error: e.message };
  return { ok: false, status: 0, error: e instanceof Error ? e.message : String(e) };
}

async function wrap<T>(fn: () => Promise<T>): Promise<IpcResult<T>> {
  try {
    return { ok: true, data: await fn() };
  } catch (e) {
    return fail(e);
  }
}

function base64ToBlob(b64: string, type = 'application/octet-stream'): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName.replace(/[\\/:*?"<>|]/g, '_') || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
  svg: 'image/svg+xml', txt: 'text/plain', csv: 'text/plain', json: 'application/json', mp4: 'video/mp4', mp3: 'audio/mpeg',
};

/**
 * The attachment as the server describes it. The name and type come from the stored message, not from the caller,
 * so a file can't be relabelled to get it opened.
 */
async function describeAttachment(messageId: string, index: number) {
  const info = (await client.getMessage(messageId)).attachments[index - 1];
  if (!info) throw new Error('Attachment not found.');
  return { ...info, safeName: safeAttachmentName(info.fileName), risky: isRiskyAttachment(info.fileName, info.contentType) };
}

// ---------- events (server push via the device's Storage Queue) ----------

const eventMsg = emitter<[MailEvent]>();
const eventStatus = emitter<['connected' | 'error' | 'stopped', string | undefined]>();
const openMsg = emitter<[string, string | undefined]>();
let poller: EventPoller | null = null;
let status: { state: 'connected' | 'error' | 'stopped'; detail?: string } = { state: 'stopped' };
let notificationsEnabled = true;

async function startEvents() {
  if (poller || !(await sessionStore.load())) return;
  poller = new EventPoller({
    subscribe: () => client.subscribeEvents(deviceId(), 'web'),
    onEvent: (e) => {
      eventMsg.emit(e);
      if (e.type === 'mail.received' && e.data && !e.data.isJunk) notifyNewMail(e);
    },
    onStatus: (state, detail) => {
      if (state !== status.state) eventStatus.emit(state, detail);
      status = { state, detail };
    },
    minDelay: 1500,
    // Background tabs are throttled by the browser anyway; keep idle polling modest.
    maxDelay: 15_000,
  });
  poller.start();
}

async function stopEvents(unsubscribe = false) {
  poller?.stop();
  poller = null;
  status = { state: 'stopped' };
  eventStatus.emit('stopped', undefined);
  if (unsubscribe) await client.unsubscribeEvents(deviceId()).catch(() => undefined);
}

window.addEventListener('focus', () => poller?.nudge());
document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && poller?.nudge());

function notifyNewMail(e: MailEvent) {
  if (!notificationsEnabled || !('Notification' in window) || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return; // the app shows an in-app toast instead
  const from = displayName(parseMailbox(e.data.from ?? '') ?? { address: e.data.fromAddress ?? '' }) || 'New email';
  const n = new Notification(from, { body: e.data.subject || '(no subject)', icon: '/icon-256.png', tag: e.data.id });
  n.onclick = () => {
    window.focus();
    openMsg.emit(e.data.id, e.data.folder);
    n.close();
  };
}

/** Browsers only show the permission prompt in response to a user action, so ask on the next click. */
function requestNotificationPermission() {
  if (!('Notification' in window) || Notification.permission !== 'default') return;
  window.addEventListener('pointerdown', () => void Notification.requestPermission().catch(() => undefined), { once: true, capture: true });
}

// ---------- mailto ----------

const mailto = emitter<[string]>();
let pendingMailto: string | null = null;

function deliverMailto(url: string) {
  if (mailto.size) mailto.emit(url);
  else pendingMailto = url;
}

// Opened as the browser's mail handler: /?mailto=mailto%3Aname%40example.com
{
  const params = new URLSearchParams(location.search);
  const m = params.get('mailto');
  if (m?.toLowerCase().startsWith('mailto:')) {
    pendingMailto = m;
    params.delete('mailto');
    const qs = params.toString();
    history.replaceState(null, '', `${location.pathname}${qs ? `?${qs}` : ''}${location.hash}`);
  }
}

// mailto: links anywhere in the app open our composer instead of another mail program.
document.addEventListener('click', (e) => {
  const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (a && a.href.toLowerCase().startsWith('mailto:')) {
    e.preventDefault();
    deliverMailto(a.href);
  }
}, true);

function registerMailHandler() {
  try {
    navigator.registerProtocolHandler?.('mailto', `${location.origin}/?mailto=%s`);
  } catch {
    /* not allowed on this origin (http) or by this browser */
  }
}

// ---------- keyboard commands (the desktop app's menu accelerators) ----------

const command = emitter<[string]>();
// Browsers reserve Ctrl+N / Ctrl+1..9 for windows and tabs, so those commands use Alt here.
window.addEventListener('keydown', (e) => {
  const mod = e.ctrlKey || e.metaKey;
  const key = e.key.toLowerCase();
  let cmd: string | null = null;
  if (e.altKey && !mod && e.code === 'KeyN') cmd = e.shiftKey ? 'compose-ai' : 'compose';
  else if (e.altKey && !mod && e.code === 'Digit1') cmd = 'go-today';
  else if (e.altKey && !mod && e.code === 'Digit2') cmd = 'go-inbox';
  else if (e.altKey && !mod && e.code === 'Digit3') cmd = 'go-contacts';
  else if (mod && !e.shiftKey && !e.altKey && key === ',') cmd = 'go-settings';
  else if ((mod && !e.shiftKey && key === 'r') || (e.key === 'F5' && !mod)) cmd = 'refresh';
  if (cmd && command.size) {
    e.preventDefault();
    command.emit(cmd);
  }
});

// ---------- badge ----------

const BASE_TITLE = 'DTS Mail';
function setBadge(count: number) {
  document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${BASE_TITLE}` : BASE_TITLE;
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (count > 0) void nav.setAppBadge?.(count).catch(() => undefined);
  else void nav.clearAppBadge?.().catch(() => undefined);
}

// ---------- CRM agent (MCP tools run in this page) ----------

const crmStep = emitter<Parameters<Parameters<DtsBridge['crm']['onStep']>[0]>>();
const crmApproval = emitter<[CrmApprovalRequest]>();
const pendingApprovals = new Map<string, (approved: boolean) => void>();

function respondApproval(requestId: string, approved: boolean) {
  pendingApprovals.get(requestId)?.(approved);
  pendingApprovals.delete(requestId);
}

// ---------- the bridge ----------

const streams = new Set<AbortController>();
const disabledUpdates: UpdateState = { state: 'disabled' };

const bridge: DtsBridge = {
  platform: 'web',
  version: __APP_VERSION__,
  auth: {
    session: () => publicSession(),
    login: (baseUrl, email, password) => wrap(() => login(baseUrl, email, password)),
    acceptInvite: (baseUrl, email, code, password) => wrap(() => acceptInvite(baseUrl, email, code, password)),
    verifyMfa: (baseUrl, mfaToken, code) =>
      wrap(async () => {
        const s = await verifyMfa(baseUrl, mfaToken, code);
        void startEvents();
        registerMailHandler();
        return s;
      }),
    enableMfa: (baseUrl, mfaToken, code) =>
      wrap(async () => {
        const r = await enableMfa(baseUrl, mfaToken, code);
        void startEvents();
        registerMailHandler();
        return r;
      }),
    logout: async () => {
      await stopEvents(true);
      await logout();
    },
    onExpired: (cb) => onSessionExpired(cb),
  },
  api: {
    request: (req) => wrap(() => transport.request(req)),
    stream: (req, onEvent) => {
      const ac = new AbortController();
      streams.add(ac);
      const done = wrap(() => transport.stream(req, onEvent, ac.signal)).finally(() => streams.delete(ac));
      return { cancel: () => ac.abort(), done };
    },
  },
  files: {
    saveAttachment: (messageId, index) =>
      wrap(async () => {
        const a = await describeAttachment(messageId, index);
        download(base64ToBlob(await client.attachmentBase64(messageId, index)), a.safeName);
        return a.safeName;
      }),
    openAttachment: (messageId, index) =>
      wrap(async () => {
        const a = await describeAttachment(messageId, index);
        // Files that can run code are never opened from an email; the user may still save one deliberately.
        if (a.risky) {
          const save = window.confirm(
            `“${a.safeName}” can run programs on your computer.\n\n` +
              "DTS Mail doesn't open this kind of file from an email, because that is how most malware spreads. " +
              'Save it only if you trust the sender and were expecting it. Your computer will still check it before it runs.\n\n' +
              'Save it to your downloads?',
          );
          if (!save) return null;
          download(base64ToBlob(await client.attachmentBase64(messageId, index)), a.safeName);
          return a.safeName;
        }
        // Open in a new tab when the browser can show it (PDF, images, text); download anything else.
        const type = MIME_BY_EXT[extensionOf(a.safeName)];
        const blob = base64ToBlob(await client.attachmentBase64(messageId, index), type);
        if (!type) {
          download(blob, a.safeName);
          return a.safeName;
        }
        const url = URL.createObjectURL(blob);
        if (!window.open(url, '_blank', 'noopener')) download(blob, a.safeName);
        setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
        return null;
      }),
    saveText: async (defaultName, content) => {
      download(new Blob([content], { type: 'text/plain;charset=utf-8' }), defaultName);
      return defaultName;
    },
  },
  uploads: {
    available: () => uploadsAvailable(),
    upload: (fileName, contentType, data) => wrap(() => uploadFile(fileName, contentType, data)),
  },
  mcp: {
    list: async () => mcp.list(),
    save: (config) => mcp.save(config),
    remove: (id) => mcp.remove(id),
    reconnect: (id) => mcp.reconnect(id),
    test: (config) => wrap(() => mcp.test(config)),
  },
  crm: {
    run: (task, opts) =>
      wrap(async () => {
        const taken = new Set<string>();
        const tools: AgentTool[] = (await mcp.allTools()).map(({ server, tool }) => ({
          name: agentToolName(server.name, tool.name, taken),
          serverId: server.id,
          serverName: server.name,
          toolName: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
          readOnly: tool.readOnly,
          destructive: tool.destructive,
        }));
        return runCrmAgent(task, tools, {
          callModel: (body) => client.agentTurn(body),
          callTool: (tool, args) => mcp.callTool(tool.serverId, tool.toolName, args),
          approve: (tool, args) => {
            if (opts.mode === 'auto' && !tool.destructive) return Promise.resolve(true);
            const requestId = crypto.randomUUID();
            return new Promise<boolean>((resolve) => {
              pendingApprovals.set(requestId, resolve);
              crmApproval.emit({ requestId, serverName: tool.serverName, tool: tool.toolName, description: tool.description, args });
              setTimeout(() => pendingApprovals.has(requestId) && respondApproval(requestId, false), 5 * 60_000);
            });
          },
          onStep: (step) => crmStep.emit(opts.runId, step),
        });
      }),
    onStep: (cb) => crmStep.on(cb),
    onApproval: (cb) => crmApproval.on(cb),
    respond: respondApproval,
    call: (tool, args) =>
      wrap(async () => {
        const r = await mcp.callByName(String(tool), args ?? {});
        if (!r) throw new Error('No connected CRM offers this feature.');
        if (r.isError) throw new Error(r.text || 'The CRM returned an error.');
        return { text: r.text, data: r.structured };
      }),
  },
  app: {
    setBadge: (count) => setBadge(count),
    focus: () => window.focus(),
    openExternal: (url) => {
      if (/^https?:/i.test(url)) window.open(url, '_blank', 'noopener,noreferrer');
      else if (/^mailto:/i.test(url)) deliverMailto(url);
    },
    setTheme: (theme) => {
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#08080f' : '#f6f6fb');
    },
    onMailto: (cb) => {
      const off = mailto.on(cb);
      if (pendingMailto) {
        const url = pendingMailto;
        pendingMailto = null;
        setTimeout(() => cb(url), 0);
      }
      return off;
    },
    onCommand: (cb) => command.on(cb),
  },
  events: {
    onEvent: (cb) => eventMsg.on(cb),
    onStatus: (cb) => eventStatus.on(cb),
    onOpen: (cb) => openMsg.on(cb),
    status: async () => status,
    test: () => wrap(() => client.testEvent()),
    setNotifications: (enabled) => {
      notificationsEnabled = enabled;
      if (enabled) requestNotificationPermission();
    },
  },
  updates: {
    // The website is always the latest version; there is nothing to install.
    state: async () => disabledUpdates,
    onState: () => () => undefined,
    check: () => undefined,
    install: () => location.reload(),
  },
  win: {
    minimize: () => undefined,
    toggleMaximize: () => undefined,
    close: () => undefined,
  },
};

window.dts = bridge;

void publicSession().then((s) => {
  if (!s) return;
  void startEvents();
  registerMailHandler();
});
